import type { Moment } from "moment";
import moment from "moment";
import { getCityStateCountry } from "./addressUtils";
import { formatArrivalTime } from "./flightUtils";
import type { ShowTimeDisplay } from "./showDateTimeDisplay";

export interface EventHeaderTexts {
  tourName: string | undefined;
  title: string;
  subtitle: string | undefined;
  location: string | undefined;
}

// The event name takes the title spot, falling back to the venue and then the tour.
// The venue only moves to the subtitle when the event name is shown as title.
const getEventHeaderTexts = (eventName: string | undefined, venueName: string | undefined, tourName: string | undefined, location: string | undefined): EventHeaderTexts => {
  const shownEventName = eventName?.trim() ? eventName : undefined;
  const shownVenueName = venueName?.trim() ? venueName : undefined;

  return {
    tourName,
    title: shownEventName ?? shownVenueName ?? tourName ?? "...",
    subtitle: shownEventName && shownVenueName ? shownVenueName : undefined,
    location
  };
};

export const getShowHeaderTexts = (show: server.dto.Show): EventHeaderTexts =>
  getEventHeaderTexts(show.eventName, show.venue?.name, show.tour?.name, show.venue ? getCityStateCountry(show.venue.address) || undefined : undefined);

export const getShowAppointmentHeaderTexts = (show: server.dto.ShowAppointment): EventHeaderTexts =>
  getEventHeaderTexts(show.eventName, show.venueName, show.tourName, show.venueLocation);

export const getExternalEventHeaderTitle = (externalEvent: server.dto.ExternalEvent): string =>
  externalEvent.summary.trim() ? externalEvent.summary : "...";

// Reduces the show time model to a single line. The fallback time and a range that
// ends on a later day both render the same way in the header.
export const formatShowTimeLabel = (time: ShowTimeDisplay): string => {
  if (time.kind === "none") return "";
  if (time.kind === "fallback") return time.text;
  if (time.kind === "single") return time.start;
  return `${time.start}-${time.end}`;
};

// When the end day differs from the start day the header shows the offset (e.g. "+1")
// next to the end time instead of the full end date.
export const formatDayOffset = (start: Date | string | Moment | undefined, end: Date | string | Moment | undefined): string | undefined => {
  if (!start || !end) return undefined;
  const [isSameDay, timeDiffInDays, sign] = formatArrivalTime(moment.utc(start), moment.utc(end));
  return isSameDay ? undefined : `${sign}${timeDiffInDays}`;
};
